/**
 * A2 (round 8 persona audit) — shared focus management for the inline
 * add-watcher / invite-checker / rule panels on TradeDetailPage (none of them
 * had it before: opening one left focus wherever the trigger button was, Esc
 * did nothing beyond DrPanel's existing case, and Tab could wander out into
 * the rest of the page while the panel was "open").
 *
 * Attach the returned `ref` to the panel's outermost element. While `active`:
 *   - focus moves to the first focusable element inside the panel
 *   - Escape calls `onClose` (the panel's own close handler)
 *   - Tab / Shift+Tab wrap within the panel's focusable elements
 * On close (active flips back to false, or the component unmounts while
 * active), focus returns to whatever had it before the panel opened — the
 * trigger button, in every current caller.
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap<T extends HTMLElement>(active: boolean, onClose: () => void) {
  const ref = useRef<T | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  // Every current caller passes an inline `() => setXOpen(false)` (a fresh
  // function each render — some of these panels hold form state that changes
  // on every keystroke while open). Reading onClose via a ref instead of a
  // hook dependency means the effect below only (re)runs when `active`
  // flips, not on every parent re-render — otherwise typing a character
  // would tear the listener down and re-steal focus to the first field.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Tracks whether we actually succeeded in moving focus into the panel —
  // checked (not live DOM containment) at cleanup time, because by the time
  // an unmount's cleanup runs the panel's DOM is typically already detached
  // and the browser has already forced `document.activeElement` to `body`,
  // which made a live "is focus still inside?" check always false and the
  // trigger button never got focus back after Escape / Cancel.
  const focusMovedIn = useRef(false);

  useEffect(() => {
    if (!active) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    focusMovedIn.current = false;

    const container = ref.current;
    const focusables = () =>
      container ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];

    // Defer to the next tick so the panel's own contents (rendered this same
    // pass) are in the DOM before we try to find something to focus.
    const t = window.setTimeout(() => {
      const first = focusables()[0];
      if (first) {
        first.focus();
        focusMovedIn.current = true;
      }
    }, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container?.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(t);
      container?.removeEventListener('keydown', onKeyDown);
      // Restore focus to the trigger as long as we're the ones who moved it
      // into the panel in the first place — calling .focus() on a
      // since-detached trigger (e.g. the whole page navigated away) is a
      // harmless no-op, so this doesn't need to re-check liveness.
      if (focusMovedIn.current) {
        triggerRef.current?.focus();
      }
    };
    // eslint-disable-next-line -- onClose intentionally excluded, see onCloseRef above
  }, [active]);

  return ref;
}
